import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { UserContext } from '../../../../context/UserContext'
import { RequestContext } from '../../../../context/RequestContext'
import { GtagContext } from '../../../../pages/_app'
import { formatDateReadable, to12HourFormat } from '../../../../utils/time-converter'
import { SystemError } from '../../../error/SystemError'
import { LoadingCover } from '../../../commons/LoadingCover'
import { GtagLoader, DateTimeRow, ServiceSummaryCard, NavigationButtons } from '../../../commons/BookingFormComponents'
import { CheckCircle } from 'lucide-react'
import { computePackagePrice, getPtModifiers } from '../../../../utils/pt-package-price'
import { getMarketByName } from '../../../../utils/markets'
import { formatMoney } from '../../../../utils/format-money'

// "$89" for Singapore (unchanged), "AED 179" / "RM129" for the new markets.
const moneyFor = (countryCd) => (v) => (countryCd === 'sg' ? `$${v}` : formatMoney(v, countryCd, 0))

// Compact dark Price Card for PT package – peak-aware
const PtPackagePriceCard = ({ selectedPackage, packagePrice, serviceCost, originalCost, totalDiscountPercent, currencyCode, money = moneyFor('sg') }) => {
    if (!selectedPackage || !packagePrice) return null;
    return (
        <div className="bg-slate-900 backdrop-blur-md rounded-2xl border border-white/10 p-4">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
                {/* Per-session price */}
                <div className="flex items-baseline justify-center gap-1">
                    <span className="text-2xl md:text-3xl font-extrabold text-white">{money(packagePrice.perSessionPrice)}</span>
                    <span className="text-sm text-white/60">/session</span>
                </div>
                {/* {packagePrice.onlineDiscountPerSession > 0 && (
                    <div className="mt-1 text-xs text-white/60">
                        <span className="line-through">{money(packagePrice.grossPerSessionPrice)}/session</span>
                        <span className="ml-1 text-green-300">(−{money(packagePrice.onlineDiscountPerSession)} online)</span>
                    </div>
                )} */}

                {/* Sessions + Duration pills */}
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/10 text-white rounded-full text-xs font-medium border border-white/15">
                        {selectedPackage.totalServices} Sessions
                    </span>
                    {/* <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/10 text-white rounded-full text-xs font-medium border border-white/15">
                        {selectedPackage.packageDuration} {selectedPackage.packageDurationUnit}s
                    </span> */}
                </div>

                {/* Total + Savings */}
                {/* <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="flex items-baseline justify-center gap-2">
                        <span className="text-xs text-white/50 uppercase tracking-wider">Total</span>
                        <span className="text-lg font-bold text-white">{money(packagePrice.totalCost)}</span>
                        {packagePrice.totalSavings > 0 && (
                            <span className="text-xs text-white/40 line-through">{money(packagePrice.originalTotalCost)}</span>
                        )}
                    </div>
                    {packagePrice.totalSavings > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-300 text-xs font-semibold">
                            Exclusive Online Booking Discount – Save {money(packagePrice.totalSavings)}
                        </div>
                    )}
                </div> */}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
                {packagePrice.isPeak ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs font-semibold border border-amber-500/30">
                        🕐 Peak +{money(packagePrice.peakPremium)}/session
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-semibold border border-green-500/30">
                        ☀️ Off-peak rate
                    </span>
                )}
                {packagePrice.isFemaleTrainer && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-pink-500/20 text-pink-200 rounded-full text-xs font-semibold border border-pink-500/30">
                        ♀ Female trainer +{money(packagePrice.femalePremium)}/session
                    </span>
                )}
            </div>
            {packagePrice.isFemaleTrainer && (
                <p className="mt-3 text-center text-xs text-white/70">
                    Remove gender preference for lower pricing
                </p>
            )}
        </div>
    );
};

export const TrainerBookingDateTime = (props) => {
    
    const goBack = props.goBack
    const goToSection4 = props.goToSection4    
    const countryName = props.countryName
    const countryCurrCd = props.countryCurrCd

    const [ minAllowed, setMinAllowed ] = useState("08:00")
    const [ maxAllowed, setMaxAllowed ] = useState("20:00")
    const [ minDate, setMinDate ] = useState("")
    const [ maxDate, setMaxDate ] = useState("")
    const isInitialLoaded = useRef(false)

    const [ displayServiceDate, setDisplayServiceDate ] = useState("")
    const { isGtagLoaded } = useContext(GtagContext)
    const [ newUserDiscountPercent, setNewUserDiscountPercent ] = useState(0)
    const [ discountPercent, setDiscountPercent ] = useState(0)
    //state to track skip option
    const [ skipDateTime, setSkipDateTime ] = useState(false);

    const currentDate = new Date().toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10)

    const [ isLoading, setIsLoading ] = useState(false)
    const [ isSystemError, setIsSystemError ] = useState(false)
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""}) 

    const { 
        requestCriteria, 
        requestType, 
        serviceDate, 
        setServiceDate,
        startTime,
        setStartTime,
        endTime,
        setEndTime, 
        serviceCost,
        setServiceCost, 
        serviceSummary, 
        setServiceSummary,
        totalDiscountPercent,
        setTotalDiscountPercent,
        originalCost,
        setOriginalCost,
        selectedPackage
    } = useContext(RequestContext);

    // Peak-aware + trainer-gender live price for the chosen package + schedule,
    // with the modifier amounts of the visitor's market.
    const priceCd = getMarketByName(countryName)?.countryCd ?? 'sg'
    const money = moneyFor(priceCd)
    const packagePrice = selectedPackage
        ? computePackagePrice(selectedPackage, serviceDate, startTime, requestCriteria, { countryCd: priceCd })
        : null;

    // Date & time are mandatory whenever a package is selected
    const isPackageMode = !!selectedPackage;    
    const serviceHours = isPackageMode ? selectedPackage.perServiceTime : 0.5; // default 30-min consultation if no package

    useEffect(() => {
        if (!selectedPackage) return;
        const { totalCost } = computePackagePrice(selectedPackage, serviceDate, startTime, requestCriteria, { countryCd: priceCd });
        setServiceCost(totalCost);
        setOriginalCost(totalCost);
    }, [selectedPackage, serviceDate, startTime, requestCriteria, setServiceCost, setOriginalCost, priceCd]);

    // In package mode, force-skip-off so user must pick date/time
    useEffect(() => {
        if (isPackageMode && skipDateTime) setSkipDateTime(false);
    }, [isPackageMode, skipDateTime]);

    const { userId } = useContext(UserContext)

    //TRAINER COST CALCULATION
    const [ servicingCost, setServicingCost ] = useState(0)
    const [ newUserServicingCost, setNewUserServicingCost ] = useState(0)
    const [ gasCost, setGasCost ] = useState(0)
    const [ newUserGasCost, setNewUserGasCost ] = useState(0)
    const [ rateAggregatorFactor, setRateAggregatorFactor ] = useState("")

    const handleServiceDateChange = (e) => {
        setServiceDate(e.target.value)
        setDisplayServiceDate(new Date(e.target.value).toString().substring(0,16))
    }

    const handleStartTimeChange = (e) => {
        setStartTime(e.target.value)
    }

    useEffect(() => {
        if (!startTime || !serviceHours) return;

        const [startH, startM] = startTime.split(":").map(Number);
        
        // Ensure serviceDuration is treated as a float (e.g., 0.5 for 30 mins)
        const serviceDuration = parseFloat(serviceHours);

        // Calculate total minutes from the start of the day
        const totalStartMinutes = startH * 60 + startM;
        const durationMinutes = Math.round(serviceDuration * 60);
        const totalEndMinutes = totalStartMinutes + durationMinutes;

        // Convert back to 24h format, handling midnight rollover
        const endH = Math.floor(totalEndMinutes / 60) % 24;
        const endM = totalEndMinutes % 60;

        const formattedEndTime = `${endH.toString().padStart(2, "0")}:${endM.toString().padStart(2, "0")}`;
        
        setEndTime(formattedEndTime);

        const label = selectedPackage
            ? `${selectedPackage.trainerCategory} Trainer - ${selectedPackage.packageName || (selectedPackage.totalServices + ' Sessions')}`
            : "Non-obligatory consultation";

        if (skipDateTime) {
            setServiceSummary(`${label} - Date/Time not selected yet`);
        } else {
            const timeRange = `${to12HourFormat(startTime)} to ${to12HourFormat(formattedEndTime)}`;
            setServiceSummary(`${label} - ${formatDateReadable(serviceDate)}, ${timeRange}`);
        }

    }, [startTime, serviceDate, setServiceSummary, setEndTime, serviceHours, skipDateTime, selectedPackage]);


    const submit = (e) => {
        e.preventDefault()

        if (!skipDateTime) {
            if (serviceDate) {
                let requestCriteriaW = {
                    'criteria': "Session Date - " + displayServiceDate,
                    [99]: serviceDate
                };
                requestCriteria.push(requestCriteriaW);
            }

            if (startTime && endTime) {
                let requestCriteriaW = {
                    'criteria': "Time slot - " + to12HourFormat(startTime) + " to " + to12HourFormat(endTime),
                    [100]: to12HourFormat(startTime) + " to " + to12HourFormat(endTime)
                };
                requestCriteria.push(requestCriteriaW);
            }
        } else {
            requestCriteria.push({
                'criteria': "Session Date/Time - Skipped",
                [101]: "Skipped"
            });
        }

        setTotalDiscountPercent(newUserDiscountPercent ? (discountPercent + newUserDiscountPercent).toFixed(2) : discountPercent)

        // Package mode: serviceCost / originalCost are already kept in sync via the useEffect above.
        if (selectedPackage) {
            goToSection4()
            return
        }

        if(servicingCost > 0){
            if(gasCost > 0 ){
                if(newUserDiscountPercent > 0){
                    const cost = (parseFloat(newUserServicingCost) + parseFloat(newUserGasCost)).toFixed(2)
                    console.log(cost)
                    setServiceCost(cost)
                } else {
                    const cost = (parseFloat(servicingCost) + parseFloat(gasCost)).toFixed(2)
                    setServiceCost(cost)
                }
            } else {
                if(newUserDiscountPercent > 0){
                    setServiceCost(newUserServicingCost)
                } else {
                    setServiceCost(servicingCost) 
                }
            }
        } else {
            if(gasCost > 0 ){
                if(newUserDiscountPercent > 0){
                    setServiceCost(newUserGasCost)
                } else {
                    setServiceCost(gasCost)
                }
            }
        }

        goToSection4()
    }
    
    const ptCostCalculatorApi = () => {
    }

    const refresh = useCallback(() => {
        if(isInitialLoaded.current) return
        ptCostCalculatorApi()
    }, [isInitialLoaded])

    useEffect(() => {
        if(!requestCriteria) return
        if(!isGtagLoaded) return

        if(!isInitialLoaded.current){
            isInitialLoaded.current = true
            ptCostCalculatorApi()
        } else {
            ptCostCalculatorApi()
        }
    }, [requestCriteria, isGtagLoaded])

    useEffect(() => {
        if(!countryName || !requestType) return

        const getServiceCategorySettings = async() => {
            try {                
                // Trainer requests need at least 3 days lead time from today
                const minD = new Date()
                minD.setDate(minD.getDate() + 3)
                setMinDate(minD.toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10))

                // Default selected date = first weekday on/after minD (skip Sat/Sun)
                const sDate = new Date(minD.getTime())
                if (sDate.getDay() === 6) sDate.setDate(sDate.getDate() + 2) // Sat -> Mon
                if (sDate.getDay() === 0) sDate.setDate(sDate.getDate() + 1) // Sun -> Mon

                setServiceDate(sDate.toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10))
                setDisplayServiceDate(sDate.toString().substring(0,16))

                const maxD = new Date()
                maxD.setDate(maxD.getDate() + 182)
                setMaxDate(maxD.toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10))

                setStartTime("10:00")
                setEndTime("10:30")

            } catch (error) {
                console.log(error)
            }
        }
        
        getServiceCategorySettings();

    }, [setServiceDate, setStartTime, setEndTime, countryName, requestType])

    return (
        <>
            <form className="max-w-xl mx-auto py-4" onSubmit={(e) => submit(e)}>
                {/* Loading State */}
                <GtagLoader isLoading={!isGtagLoaded} />
                
                {/* Header */}
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-2">
                    {isPackageMode ? "Pick your first session" : "Free Consultation"}
                </h3>
                {/* <p className='text-gray-500 text-center mb-4 text-sm'>
                    {isPackageMode
                        ? "Date & time are required so we can confirm pricing"
                        : "Preferred time for consultation"}
                </p> */}

                {/* Package Price Card (package mode only) */}
                {isPackageMode && (
                    <div className="mb-4">
                        <PtPackagePriceCard
                            selectedPackage={selectedPackage}
                            packagePrice={packagePrice}
                            serviceCost={serviceCost}
                            originalCost={originalCost}
                            totalDiscountPercent={totalDiscountPercent}
                            currencyCode={countryCurrCd}
                            money={money}
                        />
                        {/* <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700">
                            <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>Off-peak: Mon–Fri 9am–4pm. Peak slots add {money(packagePrice?.peakPremium ?? getPtModifiers(priceCd).peak)}/session.</span>
                        </div> */}
                    </div>
                )}

                {/* Skip Checkbox - consultation flow only (date/time mandatory in package mode) */}
                {!isPackageMode && (
                    <div className="mb-6">
                        <label 
                            htmlFor="skipDateTime" 
                            className={`
                                flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200
                                ${skipDateTime 
                                    ? 'bg-orange-50 border-2 border-orange-200' 
                                    : 'bg-gray-50 border-2 border-transparent hover:border-gray-200'
                                }
                            `}
                        >
                            <div className={`
                                w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors duration-200
                                ${skipDateTime 
                                    ? 'bg-linear-to-tr from-orange-600 to-amber-400 border-transparent' 
                                    : 'bg-white border-gray-300'
                                }
                            `}>
                                {skipDateTime && <CheckCircle className="w-3 h-3 text-white" />}
                            </div>
                            <input 
                                type="checkbox" 
                                id="skipDateTime" 
                                checked={skipDateTime} 
                                onChange={() => setSkipDateTime(!skipDateTime)} 
                                className="hidden"
                            />
                            <span className="text-sm text-gray-600">
                                I&apos;m not sure yet, skip date & time
                            </span>
                        </label>
                    </div>
                )}
                
                {/* Date & Time Selection - compact one row with min-height to prevent layout shift */}
                <div className="mb-4 px-2">
                    {(isPackageMode || !skipDateTime) && (
                        <DateTimeRow
                            serviceDate={serviceDate}
                            onDateChange={(e) => handleServiceDateChange(e)}
                            minDate={minDate}
                            maxDate={maxDate}
                            startTime={startTime}
                            onStartTimeChange={(e) => handleStartTimeChange(e)}
                            minAllowed={minAllowed}
                            maxAllowed={maxAllowed}
                        />
                    )}
                </div>
                
                {/* Service Summary */}
                <ServiceSummaryCard summary={serviceSummary} />
                
                {/* Navigation Buttons */}
                <NavigationButtons
                    onBack={() => goBack()}
                    showNext={rateAggregatorFactor !== "cl"}
                    promoText={isPackageMode
                        ? `Additional ${money(selectedPackage.totalServices * getPtModifiers(priceCd).online)} Off`
                        // The "30% more affordable" comparison is Singapore's.
                        : (priceCd === 'sg' ? "30% More Affordable" : "No gym membership needed")}
                />
            </form>
            
            {/* Scripts removed - now loaded globally in _app.js */}
            {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
            {isLoading && <LoadingCover/>}
        </>
    )
}